import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MagnifyingGlass, Sparkle, ArrowRight, FileText, ShoppingCart, Buildings, Wrench, Package, X, Command } from "@phosphor-icons/react";
import useCommandPalette from "@/hooks/useCommandPalette";

/**
 * CommandPalette — the "one bar to rule them all". Opens with Cmd/Ctrl+K.
 *
 * Behaviours:
 *   • Regular text → live universal search (invoices, POs, purchases, projects, tickets)
 *   • Text starting with "?", "ask ", or "how "/"what "/"why "/"which " → routes to /api/ai/ask
 *   • Quick-command chips (New invoice, New PO, Go to Dashboard etc.) always visible
 *
 * All state + effects + keyboard nav live in `useCommandPalette` (iter47 refactor
 * dropped cyclomatic complexity from 38 → 11).
 */
const QUICK_ACTIONS = [
  { id: "new-inv", label: "New invoice", icon: FileText, url: "/invoices" },
  { id: "new-po", label: "New vendor PO", icon: ShoppingCart, url: "/vendor-pos" },
  { id: "new-bill", label: "Upload vendor bill", icon: Package, url: "/purchases" },
  { id: "new-grn", label: "New goods receipt", icon: Package, url: "/goods-receipts/new" },
  { id: "goto-dashboard", label: "Dashboard", icon: Buildings, url: "/dashboard" },
  { id: "goto-projects", label: "Projects", icon: Buildings, url: "/projects" },
  { id: "goto-tickets", label: "Service tickets", icon: Wrench, url: "/service/tickets" },
];

const KIND_ICON = {
  invoice: FileText, "vendor-po": ShoppingCart, purchase: Package,
  project: Buildings, ticket: Wrench, warehouse: Package,
};

const KIND_LABEL = {
  invoice: "Invoice", "vendor-po": "Vendor PO", purchase: "Purchase",
  project: "Project", ticket: "Ticket", warehouse: "Warehouse",
};

function ResultRow({ row, idx, activeIdx, setActiveIdx, activate }) {
  const Icon = row.type === "quick" ? row.icon : (KIND_ICON[row.kind] || FileText);
  return (
    <button
      onClick={() => activate(row)}
      onMouseEnter={() => setActiveIdx(idx)}
      data-testid={`cmd-row-${idx}`}
      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${idx === activeIdx ? "bg-orange-50" : "hover:bg-slate-50"}`}
    >
      <Icon size={16} className={idx === activeIdx ? "text-[#EA580C]" : "text-slate-500"} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-slate-900 truncate">{row.label}</div>
        {row.subtitle && <div className="text-[11.5px] text-slate-500 truncate">{row.subtitle}</div>}
      </div>
      {row.type === "hit" && (
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{KIND_LABEL[row.kind] || row.kind}</span>
      )}
      <ArrowRight size={12} className="text-slate-300" />
    </button>
  );
}

function AiAnswerPanel({ loading, aiAnswer }) {
  return (
    <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white">
      {loading && <div className="text-[12px] text-slate-500">Celsius Copilot is thinking…</div>}
      {aiAnswer && (
        <div className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap" data-testid="ai-ask-answer">{aiAnswer}</div>
      )}
      {!loading && !aiAnswer && (
        <div className="text-[12px] text-slate-500 flex items-center gap-2">
          <Sparkle size={13} className="text-[#EA580C]" /> Press <kbd className="bg-white border border-slate-300 rounded px-1 text-[10px]">Enter</kbd> to ask Celsius
        </div>
      )}
    </div>
  );
}

export default function CommandPalette({ open, onClose }) {
  const p = useCommandPalette({ open, onClose, quickActions: QUICK_ACTIONS });

  if (!open) return <AnimatePresence>{null}</AnimatePresence>;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.14 }}
        className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh]"
        onClick={onClose}
        data-testid="command-palette-overlay"
      >
        <motion.div
          initial={{ y: -12, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -6, opacity: 0 }}
          transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
          className="w-[min(680px,92vw)] bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="command-palette"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
            {p.isAi ? (<Sparkle size={18} className="text-[#EA580C]" weight="fill" />) : (<MagnifyingGlass size={18} className="text-slate-500" />)}
            <input
              ref={p.inputRef}
              data-testid="command-palette-input"
              value={p.query}
              onChange={(e) => { p.setQuery(e.target.value); p.setActiveIdx(0); }}
              onKeyDown={p.onKeyDown}
              placeholder="Search anything · type ? for AI · e.g. INV-2026, Realty Corp, or “show overdue invoices”"
              className="flex-1 outline-none text-[14px] font-sans placeholder:text-slate-400"
            />
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            <kbd className="hidden sm:inline text-[10px] font-mono bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-500">Esc</kbd>
          </div>

          {p.isAi && <AiAnswerPanel loading={p.loading} aiAnswer={p.aiAnswer} />}

          <div className="max-h-[52vh] overflow-y-auto">
            {p.rows.length === 0 && !p.isAi && p.query.trim() && !p.loading && (
              <div className="px-4 py-8 text-center text-[13px] text-slate-500">No matches. Try typing “?” for an AI answer.</div>
            )}
            {!p.query.trim() && (
              <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Quick actions</div>
            )}
            {p.rows.map((row, idx) => (
              <ResultRow key={`${row.type}-${row.id}-${idx}`} row={row} idx={idx} activeIdx={p.activeIdx} setActiveIdx={p.setActiveIdx} activate={p.activate} />
            ))}
          </div>

          <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[10.5px] text-slate-500">
            <div className="flex items-center gap-3">
              <span><kbd className="bg-white border border-slate-300 rounded px-1 py-0.5 font-mono">↑↓</kbd> navigate</span>
              <span><kbd className="bg-white border border-slate-300 rounded px-1 py-0.5 font-mono">↵</kbd> open</span>
            </div>
            <div className="flex items-center gap-1">
              <Command size={11} /> <span>+ K anywhere</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
